import { calcTotals } from '@florezjaramillo/florezjaramillo-core'
import { type Component, createEffect, createMemo, createResource, createSignal, For, Show } from 'solid-js'
import { createStore, produce } from 'solid-js/store'
import Button from '~/components/Button'
import Card from '~/components/Card'
import DateInput from '~/components/DateInput'
import Form from '~/components/Form'
import GoBack from '~/components/GoBack'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Menu from '~/components/Menu'
import Select from '~/components/Select'
import Switch from '~/components/Switch'
import Textarea from '~/components/Textarea'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useAuth } from '~/contexts/AuthContext'
import { useCompany } from '~/contexts/CompanyContext'
import { useCompanySettings } from '~/hooks/useCompanySettings'
import { useIsMobile } from '~/hooks/useIsMobile'
import DiscountField from '~/layers/ui/admin/shared/DiscountField'
import LineItemModal from '~/layers/ui/admin/shared/LineItemModal'
import LineSwipeCard from '~/layers/ui/admin/shared/LineSwipeCard'
import LineTable from '~/layers/ui/admin/shared/LineTable'
import { type DiscountTypeType, emptyLine, type LineType, lineFromService } from '~/layers/ui/admin/shared/line'
import NewClientModal, { type NewClientValuesType } from '~/layers/ui/admin/shared/NewClientModal'
import NewServiceModal, { type NewServiceValuesType } from '~/layers/ui/admin/shared/NewServiceModal'
import { useNavigate } from '~/router'
import { authService } from '~/services/auth.service'
import { companyService } from '~/services/company.service'
import { customerService } from '~/services/customer.service'
import { type IssuedByType, type PaymentAccountStatusType, type PaymentAccountType, type PaymentInfoType, paymentAccountService } from '~/services/payment-account.service'
import { serviceCatalogService } from '~/services/serviceCatalog.service'
import { resolveError } from '~/utils/error'
import { bankAccountTypeOptions, currencyOptions, formatMoney, getCurrencyLocale } from '~/utils/settings'
import { accountStatusLabels as statusLabels } from '~/utils/status'

const STATUS_OPTIONS = Object.entries(statusLabels).map(([value, title]) => ({ value, title }))

type AccountLineType = LineType

const PaymentAccountForm: Component<{ account: () => PaymentAccountType | null }> = props => {
  const toast = useToast()
  const alert = useAlert()
  const navigate = useNavigate()
  const { currentCompany } = useCompany()
  const auth = useAuth()
  const { settings } = useCompanySettings()

  /** Datos de facturación del usuario actual (para emitir a su nombre). */
  const currentUser = () => (auth.user() ?? {}) as Record<string, unknown>
  const [userProfile] = createResource(async () => {
    try {
      const data = await authService.me()
      return (data ?? {}) as Record<string, unknown>
    } catch {
      return {} as Record<string, unknown>
    }
  })
  const userBilling = createMemo(() => {
    const fresh = userProfile() ?? currentUser()
    const billing = (fresh.billingInfo ?? {}) as {
      name?: string
      document?: string
      address?: string
      phone?: string
      email?: string
      paymentInfo?: PaymentInfoType
    }
    return {
      name: billing.name ?? String(fresh.name ?? ''),
      document: billing.document ?? String(fresh.document ?? ''),
      address: billing.address ?? '',
      phone: billing.phone ?? '',
      email: billing.email ?? String(fresh.email ?? ''),
      paymentInfo: billing.paymentInfo ?? {}
    }
  })

  const [customers, { refetch: refetchCustomers }] = createResource(async () => {
    try {
      return (await customerService.list({ limit: 200 })).items
    } catch {
      return []
    }
  })
  const [catalog, { refetch: refetchCatalog }] = createResource(async () => {
    try {
      return await serviceCatalogService.list()
    } catch {
      return []
    }
  })
  const [defaults] = createResource(
    () => currentCompany().id,
    async (companyId: string) => {
      try {
        const company = await companyService.get(companyId)
        const settings = (company.settings ?? {}) as {
          paymentInfo?: PaymentInfoType
          quoteDefaults?: { currency?: string; taxRate?: number; terms?: string }
        }
        return {
          paymentInfo: settings.paymentInfo ?? null,
          currency: settings.quoteDefaults?.currency ?? 'COP',
          taxRate: settings.quoteDefaults?.taxRate ?? 0,
          terms: settings.quoteDefaults?.terms ?? ''
        }
      } catch {
        return { paymentInfo: null, currency: 'COP', taxRate: 0, terms: '' }
      }
    }
  )
  const [isSubmitting, setIsSubmitting] = createSignal(false)
  const [lines, setLines] = createStore<AccountLineType[]>([])
  const [customerId, setCustomerId] = createSignal<string>('')
  const [currency, setCurrency] = createSignal('COP')
  const [status, setStatus] = createSignal<PaymentAccountStatusType>('draft')
  const [issuedBy, setIssuedBy] = createSignal<IssuedByType>('company')
  const [dueDate, setDueDate] = createSignal('')
  const [paidAt, setPaidAt] = createSignal('')
  const [taxRate, setTaxRate] = createSignal(0)
  const [globalDiscount, setGlobalDiscount] = createSignal(0)
  const [globalDiscountType, setGlobalDiscountType] = createSignal<DiscountTypeType>('amount')
  const [titleValue, setTitleValue] = createSignal('')
  const [descriptionValue, setDescriptionValue] = createSignal('')
  const [notesValue, setNotesValue] = createSignal('')
  const [termsValue, setTermsValue] = createSignal('')
  const [initialized, setInitialized] = createSignal(false)

  // Datos bancarios: se parte de los del emisor (empresa o usuario) y se
  // pueden sobreescribir aquí para esta cuenta.
  const [useDefaultPaymentInfo, setUseDefaultPaymentInfo] = createSignal(true)
  const [paymentInfo, setPaymentInfo] = createSignal<PaymentInfoType>({})

  /** Datos bancarios por defecto según el emisor elegido. */
  const defaultPaymentInfo = createMemo(() => {
    if (issuedBy() === 'user') return userBilling().paymentInfo ?? {}
    return defaults()?.paymentInfo ?? {}
  })

  /** Nombre del usuario que firma, para mostrarlo en las opciones del emisor. */
  const myName = createMemo(() => userBilling().name || String(currentUser().name ?? 'Yo'))

  const [showClientModal, setShowClientModal] = createSignal(false)
  const [isCreatingClient, setIsCreatingClient] = createSignal(false)
  const [showServiceModal, setShowServiceModal] = createSignal(false)
  const [isCreatingService, setIsCreatingService] = createSignal(false)
  const [showLineModal, setShowLineModal] = createSignal(false)
  const [editingLineIndex, setEditingLineIndex] = createSignal<number | null>(null)
  const isMobile = useIsMobile()

  const customerOptions = createMemo(() => (customers() ?? []).map(c => ({ value: String(c.id), title: c.name, subtitle: c.document ?? undefined })))

  const selectedCustomer = createMemo(() => (customers() ?? []).find(c => String(c.id) === customerId()) ?? null)

  /** Monedas, locales y tipos de cuenta vienen de `companies.settings`. */
  const monedaOptions = createMemo(() => currencyOptions(settings()?.currencies ?? []))
  const monedaLocale = createMemo(() => getCurrencyLocale(settings()?.currencies ?? [], currency()))
  const bancoOptions = createMemo(() => bankAccountTypeOptions(settings()?.bankAccountTypes ?? []))

  const catalogOptions = createMemo(() =>
    (catalog() ?? []).filter(s => s.active).map(s => ({ value: String(s.id), title: s.name, subtitle: s.price ? `desde ${s.price.toLocaleString('es-CO')}` : undefined }))
  )

  const totals = createMemo(() => {
    const t = calcTotals(lines, taxRate(), globalDiscount(), globalDiscountType())
    return { subtotal: t.subtotal, discount: t.discountAmount, taxBase: t.taxBase, taxAmount: t.taxAmount, total: t.total }
  })

  const money = (value: number) => formatMoney(value, currency(), settings()?.currencies ?? [])

  const resetForm = () => {
    const preset = defaults() ?? { paymentInfo: null, currency: 'COP', taxRate: 0, terms: '' }
    setLines([emptyLine()])
    setCustomerId('')
    setCurrency(preset.currency ?? 'COP')
    setStatus('draft')
    setTaxRate(preset.taxRate ?? 0)
    setGlobalDiscount(0)
    setGlobalDiscountType('amount')
    setDueDate('')
    setPaidAt('')
    setIssuedBy('company')
    setUseDefaultPaymentInfo(true)
    setPaymentInfo({})
    setTitleValue('')
    setDescriptionValue('')
    setNotesValue('')
    setTermsValue(preset.terms ?? '')
  }

  const loadEditData = (account: PaymentAccountType) => {
    setLines(
      (account.items ?? [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(item => ({
          description: item.description,
          quantity: Number(item.quantity) || 0,
          unitPrice: Number(item.unitPrice) || 0,
          discount: Number(item.discount) || 0,
          discountType: (item.discountType as DiscountTypeType) ?? 'amount',
          notes: item.notes ?? ''
        }))
    )
    setCustomerId(account.customerId ? String(account.customerId) : '')
    setCurrency(account.currency)
    setStatus(account.status)
    setTaxRate(Number(account.taxRate) || 0)
    setGlobalDiscount(Number(account.discount) || 0)
    setGlobalDiscountType((account.discountType as DiscountTypeType) ?? 'amount')
    setDueDate(account.dueDate ? String(account.dueDate).slice(0, 10) : '')
    setPaidAt(account.paidAt ? String(account.paidAt).slice(0, 10) : '')
    setIssuedBy(account.issuedBy ?? 'company')
    setTitleValue(account.title)
    setDescriptionValue(account.description ?? '')
    setNotesValue(account.notes ?? '')
    setTermsValue(account.terms ?? '')
    // Si la cuenta guardó sus propios datos bancarios, se editan; si no, se
    // usan los del emisor elegido (empresa o usuario).
    if (account.paymentInfo) {
      setUseDefaultPaymentInfo(false)
      setPaymentInfo(account.paymentInfo)
    } else {
      setUseDefaultPaymentInfo(true)
      setPaymentInfo({})
    }
  }

  /**
   * Al editar hay que esperar a que llegue la cuenta. Antes se daba el
   * formulario por inicializado en cuanto había defaults y, si la petición aún
   * no había respondido, se pintaba un formulario vacío: los campos salían en
   * blanco y "Actualizar" no hacía nada.
   */
  createEffect(() => {
    if (initialized()) return

    const account = props.account()
    if (account) {
      loadEditData(account)
    } else {
      if (defaults.loading) return
      resetForm()
    }

    setInitialized(true)
  })

  const openLineModal = (index: number | null = null) => {
    setEditingLineIndex(index)
    setShowLineModal(true)
  }

  /** Guarda la línea: nueva al final o reemplazando la que se está editando. */
  const saveLine = (line: LineType) => {
    const index = editingLineIndex()
    if (index != null) setLines(index, line)
    else setLines(produce(draft => draft.push(line)))
    setEditingLineIndex(null)
  }

  const removeLine = async (index: number) => {
    const ok = await alert.confirmDelete({ title: '¿Quitar línea?', subtitle: 'Se eliminará este concepto de la cuenta de cobro.' })
    if (ok) setLines(produce(draft => draft.splice(index, 1)))
  }

  const duplicateLine = (index: number) => setLines(produce(draft => draft.splice(index + 1, 0, { ...draft[index] })))

  const patchPaymentInfo = <K extends keyof PaymentInfoType>(key: K, value: PaymentInfoType[K]) => setPaymentInfo(prev => ({ ...prev, [key]: value }))

  const handleCreateClient = async (values: NewClientValuesType) => {
    setIsCreatingClient(true)
    try {
      const payload = {
        name: values.name,
        document: values.document || undefined,
        phone: values.phone || undefined,
        address: values.address || undefined
      }
      const created = await customerService.create(payload)
      toast.addToast({ title: 'Cliente creado', type: 'success' })
      await refetchCustomers()
      setCustomerId(String(created.id))
      setShowClientModal(false)
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setIsCreatingClient(false)
    }
  }

  /** Crea el servicio en el catálogo y lo añade de una vez a esta cuenta. */
  const handleCreateService = async (values: NewServiceValuesType) => {
    setIsCreatingService(true)
    try {
      const created = await serviceCatalogService.create({
        name: values.name,
        description: values.description,
        price: Number(values.price) || 0,
        unit: values.unit
      })
      toast.addToast({ title: 'Servicio creado y añadido', type: 'success' })
      await refetchCatalog()
      setLines(produce(draft => draft.push(lineFromService(created))))
      setShowServiceModal(false)
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setIsCreatingService(false)
    }
  }

  const handleSubmit = async (formData: FormData) => {
    const items = lines
      .filter(line => line.description.trim())
      .map((line, index) => ({
        description: line.description.trim(),
        quantity: Number(line.quantity) || 0,
        unitPrice: Number(line.unitPrice) || 0,
        discount: Number(line.discount) || 0,
        discountType: line.discountType,
        notes: line.notes.trim() || undefined,
        order: index
      }))

    if (items.length === 0) {
      toast.addToast({ title: 'Añade al menos un concepto a la cuenta', type: 'warning' })
      return
    }

    const paymentInfoPayload: PaymentInfoType | undefined = useDefaultPaymentInfo() ? undefined : paymentInfo()

    setIsSubmitting(true)
    try {
      const payload = {
        customerId: customerId() ? Number(customerId()) : undefined,
        title: String(formData.get('title') ?? '').trim(),
        description: String(formData.get('description') ?? '').trim() || undefined,
        currency: currency(),
        issuedBy: issuedBy(),
        taxRate: taxRate(),
        discount: globalDiscount(),
        discountType: globalDiscountType(),
        dueDate: dueDate() ? new Date(`${dueDate()}T00:00:00`).toISOString() : undefined,
        paidAt: status() === 'paid' && paidAt() ? new Date(`${paidAt()}T00:00:00`).toISOString() : undefined,
        paymentInfo: paymentInfoPayload,
        notes: String(formData.get('notes') ?? '').trim() || undefined,
        terms: String(formData.get('terms') ?? '').trim() || undefined,
        items
      }

      if (props.account()) {
        await paymentAccountService.update(props.account()!.id, { ...payload, status: status() })
        toast.addToast({ title: 'Cuenta de cobro actualizada', type: 'success' })
      } else {
        await paymentAccountService.create(payload)
        toast.addToast({ title: 'Cuenta de cobro creada', type: 'success' })
      }

      navigate('/admin/cuentas-cobro')
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const pageTitle = createMemo(() => (props.account() ? 'Editar Cuenta de Cobro' : 'Nueva Cuenta de Cobro'))

  return (
    <>
      <div class="pb-4">
        {/* Cabecera con la barra de acciones rápidas: crear cliente o servicio
            sin salir de la cuenta, y atajos a sus pantallas completas. */}
        <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between mb-6">
          <div class="flex items-center gap-3 min-w-0">
            <GoBack label="Volver a cuentas de cobro" fallbackPath="/admin/cuentas-cobro" />
            <div class="min-w-0">
              <h1 class="text-xl font-bold text-foreground truncate">{pageTitle()}</h1>
              <p class="text-sm text-foreground-subtle">Elige conceptos del catálogo o escribe uno libre</p>
            </div>
          </div>

          <div class="flex items-center gap-2 flex-wrap">
            <Button.Group variant="soft" color="default">
              <Menu
                trigger={<Button icon="material-symbols-light:more-vert" tooltip="Más acciones" />}
                items={[
                  { label: 'Nuevo cliente', icon: 'material-symbols-light:person-add-outline', onClick: () => setShowClientModal(true), color: 'primary' },
                  { label: 'Nuevo servicio', icon: 'material-symbols-light:add-business-outline', onClick: () => setShowServiceModal(true), color: 'primary' },
                  { label: 'Ver catálogo de servicios', icon: 'material-symbols-light:handyman-outline', onClick: () => navigate('/admin/servicios') },
                  { label: 'Administrar clientes', icon: 'material-symbols-light:group-outline', onClick: () => navigate('/admin/clientes') },
                  ...(props.account() ? [{ label: 'Ver PDF', icon: 'material-symbols-light:picture-as-pdf-outline', onClick: () => paymentAccountService.openPdf(props.account()!.id) }] : [])
                ]}
              />
              <Button type="submit" form="payment-account-form" color="primary" disabled={isSubmitting()} prependIcon="material-symbols-light:save-outline">
                <Show
                  when={isSubmitting()}
                  fallback={
                    <Show when={props.account()} fallback="Crear">
                      Guardar
                    </Show>
                  }
                >
                  Guardando...
                </Show>
              </Button>
            </Button.Group>
          </div>
        </div>

        <Show when={initialized()}>
          <Form id="payment-account-form" onSubmit={handleSubmit} initialValues={{ title: titleValue(), description: descriptionValue(), notes: notesValue(), terms: termsValue() }}>
            {/* Dos columnas: a la izquierda lo que se redacta, a la derecha lo
                que se consulta mientras tanto (totales y condiciones). */}
            <div class="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
              <div class="xl:col-span-2 space-y-6">
                <Card>
                  <Card.Header>
                    <h2 class="font-semibold text-foreground">Datos del cobro</h2>
                  </Card.Header>
                  <Card.Body>
                    <div class="space-y-4">
                      <Input
                        name="title"
                        label="Concepto a cobrar"
                        placeholder="Servicios de desarrollo web - marzo"
                        required
                        value={titleValue()}
                        onInput={e => setTitleValue(e.currentTarget.value)}
                      />

                      <div>
                        <div class="flex items-end gap-2">
                          <div class="flex-1 min-w-0">
                            <Select
                              label="Cliente"
                              options={customerOptions()}
                              value={customerId()}
                              onChange={v => setCustomerId(String(v ?? ''))}
                              placeholder="Sin cliente asociado"
                              autocomplete
                              clearable
                            />
                          </div>
                          <Button type="button" variant="soft" color="primary" class="shrink-0" onClick={() => setShowClientModal(true)} prependIcon="material-symbols-light:add">
                            Nuevo
                          </Button>
                        </div>
                        <Show when={selectedCustomer()}>
                          {customer => (
                            <p class="text-xs text-foreground-subtle mt-1.5 flex items-center gap-1">
                              <Icon icon="material-symbols-light:badge-outline" size={14} />
                              {[customer().document, customer().phone, customer().address].filter(Boolean).join(' · ') || 'Sin datos de contacto'}
                            </p>
                          )}
                        </Show>
                      </div>

                      <Textarea
                        name="description"
                        label="Descripción"
                        rows={2}
                        placeholder="Detalle del trabajo o productos que se cobran"
                        value={descriptionValue()}
                        onInput={e => setDescriptionValue(e.currentTarget.value)}
                      />
                    </div>
                  </Card.Body>
                </Card>

                <Show
                  when={isMobile()}
                  fallback={
                    <LineTable
                      lines={lines}
                      formatMoney={money}
                      title="Conceptos a cobrar"
                      subtitle={`${lines.length} línea(s)`}
                      addLabel="Añadir concepto"
                      onAdd={() => openLineModal()}
                      onEdit={openLineModal}
                      onDuplicate={duplicateLine}
                      onRemove={removeLine}
                    />
                  }
                >
                  <div class="flex items-center justify-between gap-3 mb-3">
                    <div>
                      <h2 class="font-semibold text-foreground">Conceptos a cobrar</h2>
                      <p class="text-xs text-foreground-subtle mt-0.5">{lines.length} línea(s)</p>
                    </div>
                    <Button type="button" size="sm" variant="soft" color="default" onClick={() => openLineModal()} prependIcon="material-symbols-light:add">
                      Añadir concepto
                    </Button>
                  </div>
                  <Show
                    when={lines.length > 0}
                    fallback={
                      <div class="py-10 text-center">
                        <Icon icon="material-symbols-light:receipt-long-outline" size={40} class="mx-auto mb-2 text-foreground-subtle" />
                        <p class="text-sm font-medium text-foreground">Todavía no has añadido conceptos</p>
                        <p class="text-xs text-foreground-subtle mt-1">Elige uno del catálogo o añade una línea libre.</p>
                      </div>
                    }
                  >
                    <div class="space-y-2">
                      <For each={lines}>
                        {(line, index) => <LineSwipeCard line={line} index={index()} formatMoney={money} onEdit={openLineModal} onDuplicate={duplicateLine} onRemove={removeLine} />}
                      </For>
                    </div>
                  </Show>
                </Show>

                <Card>
                  <Card.Header>
                    <div class="flex items-center gap-2">
                      <Icon icon="material-symbols-light:account-balance-outline" size={18} class="text-foreground-subtle" />
                      <h2 class="font-semibold text-foreground">Emisor y datos para el pago</h2>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <div class="space-y-4">
                      <Select
                        label="A nombre de"
                        value={issuedBy()}
                        onChange={v => {
                          setIssuedBy((v as IssuedByType) ?? 'company')
                          setUseDefaultPaymentInfo(true)
                          setPaymentInfo({})
                        }}
                        options={[
                          { value: 'company', title: 'La empresa', subtitle: 'Usa los datos configurados en la empresa' },
                          { value: 'user', title: `Yo (${myName()})`, subtitle: 'Usa mis datos de facturación y mi banco' }
                        ]}
                      />
                      <p class="-mt-2 text-xs text-foreground-subtle">
                        {issuedBy() === 'user'
                          ? 'El PDF saldrá a tu nombre con tus datos de facturación. Puedes editarlos desde "Mi Perfil".'
                          : 'El PDF saldrá a nombre de la empresa con sus datos configurados.'}
                      </p>

                      <p class="text-sm text-foreground-subtle">Estos datos van impresos en el PDF para que el cliente sepa dónde consignar.</p>

                      <div class="flex flex-wrap gap-2">
                        <button
                          type="button"
                          class="inline-flex items-center gap-1.5 rounded-lg bg-surface border border-border px-3 py-2 text-sm text-foreground-subtle hover:text-primary transition-colors"
                          onClick={() => navigate('/admin/perfil')}
                        >
                          <Icon icon="material-symbols-light:person-outline" size={18} />
                          Editar mis datos de facturación
                        </button>
                      </div>

                      <div>
                        <Switch
                          label={issuedBy() === 'user' ? 'Usar mi cuenta bancaria' : 'Usar los datos bancarios de la empresa'}
                          checked={useDefaultPaymentInfo()}
                          onChange={e => setUseDefaultPaymentInfo(e.currentTarget.checked)}
                        />
                      </div>

                      <Show
                        when={!useDefaultPaymentInfo()}
                        fallback={
                          <Show when={Object.values(defaultPaymentInfo()).some(Boolean)}>
                            <div class="rounded-lg border border-border bg-surface-muted px-4 py-3 text-sm text-foreground-subtle space-y-1">
                              <p class="font-medium text-foreground">{issuedBy() === 'user' ? 'Mis datos bancarios' : 'Datos de la empresa'}</p>
                              <p>
                                <strong>Banco:</strong> {defaultPaymentInfo().bankName || '—'}
                              </p>
                              <p>
                                <strong>Cuenta:</strong> {[defaultPaymentInfo().accountType, defaultPaymentInfo().accountNumber].filter(Boolean).join(' · ') || '—'}
                              </p>
                              <p>
                                <strong>Titular:</strong> {[defaultPaymentInfo().accountHolder, defaultPaymentInfo().holderDocument].filter(Boolean).join(' · ') || '—'}
                              </p>
                            </div>
                          </Show>
                        }
                      >
                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <Input label="Banco" placeholder="Bancolombia, Davivienda..." value={paymentInfo().bankName ?? ''} onInput={e => patchPaymentInfo('bankName', e.currentTarget.value)} />
                          <Select
                            label="Tipo de cuenta"
                            options={bancoOptions()}
                            value={paymentInfo().accountType ?? ''}
                            onChange={v => patchPaymentInfo('accountType', String(v ?? ''))}
                            placeholder="Selecciona..."
                            clearable
                          />
                          <Input
                            label="Número de cuenta"
                            placeholder="000-123456-78"
                            value={paymentInfo().accountNumber ?? ''}
                            onInput={e => patchPaymentInfo('accountNumber', e.currentTarget.value)}
                          />
                          <Input
                            label="Titular de la cuenta"
                            placeholder="Nombre o razón social"
                            value={paymentInfo().accountHolder ?? ''}
                            onInput={e => patchPaymentInfo('accountHolder', e.currentTarget.value)}
                          />
                          <Input
                            label="Documento del titular"
                            placeholder="NIT, cédula..."
                            value={paymentInfo().holderDocument ?? ''}
                            onInput={e => patchPaymentInfo('holderDocument', e.currentTarget.value)}
                          />
                        </div>
                      </Show>
                    </div>
                  </Card.Body>
                </Card>

                <Card>
                  <Card.Header>
                    <h2 class="font-semibold text-foreground">Notas y condiciones</h2>
                  </Card.Header>
                  <Card.Body>
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <Textarea name="notes" label="Notas" rows={4} placeholder="Aclaraciones para el cliente" value={notesValue()} onInput={e => setNotesValue(e.currentTarget.value)} />
                      <Textarea
                        name="terms"
                        label="Términos y condiciones"
                        rows={4}
                        placeholder="Forma de pago, tiempos de entrega..."
                        value={termsValue()}
                        onInput={e => setTermsValue(e.currentTarget.value)}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </div>

              {/* Resumen y condiciones: acompañan la redacción sin estorbar. */}
              <div class="space-y-6 xl:sticky xl:top-4">
                <Card color="primary" style="border">
                  <Card.Header>
                    <h2 class="font-semibold text-foreground">Resumen</h2>
                  </Card.Header>
                  <Card.Body>
                    <div class="space-y-2">
                      <div class="flex justify-between text-sm">
                        <span class="text-foreground-subtle">Subtotal</span>
                        <span class="text-foreground font-medium">{money(totals().subtotal)}</span>
                      </div>
                      <Show when={totals().discount > 0}>
                        <div class="flex justify-between text-sm">
                          <span class="text-foreground-subtle">Descuento{globalDiscountType() === 'percent' ? ` (${globalDiscount()}%)` : ''}</span>
                          <span class="text-foreground font-medium">- {money(totals().discount)}</span>
                        </div>
                      </Show>
                      <Show when={taxRate() > 0}>
                        <div class="flex justify-between text-sm">
                          <span class="text-foreground-subtle">Impuesto ({taxRate()}%)</span>
                          <span class="text-foreground font-medium">{money(totals().taxAmount)}</span>
                        </div>
                      </Show>
                      <div class="flex justify-between items-baseline pt-3 mt-1 border-t border-border">
                        <span class="font-semibold text-foreground">Total</span>
                        <span class="text-xl font-bold text-primary">{money(totals().total)}</span>
                      </div>
                    </div>
                  </Card.Body>
                </Card>

                <Card>
                  <Card.Header>
                    <h2 class="font-semibold text-foreground">Condiciones</h2>
                  </Card.Header>
                  <Card.Body>
                    <div class="space-y-4">
                      <Select label="Moneda" options={monedaOptions()} value={currency()} onChange={v => setCurrency(String(v ?? 'COP'))} />
                      <DateInput mode="date" label="Vence" value={dueDate()} onChange={value => setDueDate(value)} placeholder="Sin vencimiento" />
                      <Input label="Impuesto (%)" type="number" min="0" step="0.01" value={taxRate()} onInput={e => setTaxRate(Number(e.currentTarget.value) || 0)} />
                      <DiscountField
                        label="Descuento global"
                        value={globalDiscount()}
                        type={globalDiscountType()}
                        currency={currency()}
                        locale={monedaLocale()}
                        resolved={totals().discount}
                        formatMoney={money}
                        onValueChange={setGlobalDiscount}
                        onTypeChange={setGlobalDiscountType}
                      />
                      <Select label="Estado" options={STATUS_OPTIONS} value={status()} onChange={v => setStatus((v as PaymentAccountStatusType) ?? 'draft')} />
                      <Show when={status() === 'paid'}>
                        <DateInput mode="date" label="Fecha de pago" value={paidAt()} onChange={value => setPaidAt(value)} placeholder="Hoy" />
                      </Show>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </div>
          </Form>
        </Show>
      </div>

      <NewClientModal
        isOpen={showClientModal()}
        isSubmitting={isCreatingClient()}
        onClose={() => setShowClientModal(false)}
        onSubmit={handleCreateClient}
        subtitle="Se seleccionará en la cuenta al crearlo"
      />

      <NewServiceModal
        isOpen={showServiceModal()}
        isSubmitting={isCreatingService()}
        currency={currency()}
        locale={monedaLocale()}
        onClose={() => setShowServiceModal(false)}
        onSubmit={handleCreateService}
        subtitle="Queda en tu catálogo y se añade a esta cuenta"
      />

      <LineItemModal
        isOpen={showLineModal()}
        onClose={() => {
          setShowLineModal(false)
          setEditingLineIndex(null)
        }}
        onAdd={saveLine}
        initialLine={editingLineIndex() != null ? lines[editingLineIndex()!] : null}
        catalogOptions={catalogOptions()}
        services={catalog() ?? []}
        currency={currency()}
        locale={monedaLocale()}
        formatMoney={money}
      />
      <alert.AlertComponent />
    </>
  )
}

export default PaymentAccountForm
